// CourseScheduleMaker.js - Clean implementation from scratch

import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { CourseDesignContext } from '../context/CourseDesignContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faPlus, faTrash, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { generateClassDays, generateUniqueId } from '../utils/courseDesignUtilities';
import { validateDataOnLoad, removeDummyPairs } from '../utils/scheduleMakerUtilities';
import './CourseScheduleMaker.css';

// Simple date formatting function to avoid timezone issues
const formatDateForDisplay = (dateString) => {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return 'No date';
  }
  
  const [year, month, day] = dateString.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayName = dayNames[date.getDay()];
  
  return `${dayName}, ${parseInt(month)}/${parseInt(day)}`;
};

const CourseScheduleMaker = () => {
  const { courseDesignState, setCourseDesignState, updateDatabase, prepareStateForUpsertToDatabase, removeFromDatabase } = useContext(CourseDesignContext);
  
  // Current mode state
  const [currentMode, setCurrentMode] = useState(''); // No default selection
  
  // Edit state tracking
  const [editingItem, setEditingItem] = useState(null);
  
  // Use ref to avoid re-renders during typing
  const inputRef = useRef(null);
  
  // Track if we've already normalized data on load
  const hasNormalized = useRef(false);
  
  // Modal states
  const [showGenerateDaysModal, setShowGenerateDaysModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
  const [selectedForDeletion, setSelectedForDeletion] = useState(new Set());

  // Auto-focus for editing (skip cursor positioning for date inputs)
  useEffect(() => {
    if (editingItem) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Only set cursor position for text inputs, not date inputs
          if (inputRef.current.type !== 'date') {
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
          }
        }
      }, 10);
    }
  }, [editingItem]);

  // Simple state update function - no aggressive validation
  const updateState = useCallback((newState) => {
    setCourseDesignState(newState);
    const stateForDatabase = prepareStateForUpsertToDatabase(newState);
    updateDatabase(stateForDatabase);
  }, [setCourseDesignState, prepareStateForUpsertToDatabase, updateDatabase]);

  // Normalize schedule data on component load
  useEffect(() => {
    // Only run validation once and if we have data
    if (courseDesignState.length > 0 && !hasNormalized.current) {
      // First check for dummy pairs and delete them from database
      const dummyPairsResult = removeDummyPairs(courseDesignState);
      
      if (dummyPairsResult.modified) {
        // Get the items that were removed
        const removedItems = courseDesignState.filter(item => 
          !dummyPairsResult.state.some(newItem => newItem.dataID === item.dataID)
        );
        
        // Delete removed items from database
        removedItems.forEach(item => {
          console.log(`Deleting dummy item from database: ${item.dataType} ${item.dataID}`);
          removeFromDatabase(item.dataType, item.dataID);
        });
        
        // Now run full validation on the cleaned state
        const validatedState = validateDataOnLoad(dummyPairsResult.state);
        console.log('Normalizing schedule data on load (with dummy pair cleanup)');
        updateState(validatedState);
      } else {
        // No dummy pairs, just run normal validation
        const validatedState = validateDataOnLoad(courseDesignState);
        
        // Only update if changes were actually made
        if (JSON.stringify(validatedState) !== JSON.stringify(courseDesignState)) {
          console.log('Normalizing schedule data on load');
          updateState(validatedState);
        }
      }
      
      hasNormalized.current = true;
    }
  }, [courseDesignState, updateState, removeFromDatabase]); // Include dependencies

  // Group items for display following the three rules
  const getGroupedItems = () => {
    // Get all items and sort by dataIndex
    const topics = courseDesignState.filter(item => item.dataType === 'topic').sort((a, b) => parseFloat(a.dataIndex) - parseFloat(b.dataIndex));
    const courseDates = courseDesignState.filter(item => item.dataType === 'courseDate').sort((a, b) => parseFloat(a.dataIndex) - parseFloat(b.dataIndex));
    const sections = courseDesignState.filter(item => item.dataType === 'sectionName').sort((a, b) => parseFloat(a.dataIndex) - parseFloat(b.dataIndex));
    
    // Create grouped structure with separate date and topic items
    const grouped = [];
    let currentSection = null;
    
    // Get all indices that have content
    const allIndices = [...new Set([
      ...topics.map(t => parseFloat(t.dataIndex)),
      ...sections.map(s => parseFloat(s.dataIndex))
    ])].sort((a, b) => a - b);
    
    allIndices.forEach(index => {
      const indexStr = index.toString();
      const section = sections.find(s => s.dataIndex === indexStr);
      const topic = topics.find(t => t.dataIndex === indexStr);
      const courseDate = courseDates.find(cd => cd.dataIndex === indexStr);
      
      if (section) {
        currentSection = section;
        grouped.push({
          type: 'section',
          index: index,
          section: section,
          currentSection: currentSection
        });
      }
      
      if (topic || courseDate) {
        grouped.push({
          type: 'dateTopicPair',
          index: index,
          topic: topic,
          courseDate: courseDate,
          currentSection: currentSection
        });
      }
    });
    
    return grouped;
  };

  // Mode change handler
  const handleModeChange = (mode) => {
    // Close any open modals and clear editing state
    setShowGenerateDaysModal(false);
    setShowAddModal(false);
    setShowDeleteModal(false);
    setDeleteConfirmItem(null);
    setSelectedForDeletion(new Set());
    setEditingItem(null);
    
    if (mode === 'generateDays') {
      setShowGenerateDaysModal(true);
      setCurrentMode('edit'); // Don't actually set generateDays mode
    } else if (mode === 'add') {
      setShowAddModal(true);
      setCurrentMode('edit'); // Don't actually set add mode
    } else {
      setCurrentMode(mode);
    }
  };

  // Helper function to check if a topic has associated content
  const hasAssociatedContent = (topic) => {
    // Check if any questions have questionCourseDate matching this topic's dataID
    const hasQuestions = courseDesignState.some(item => 
      item.dataType === 'question' && item.questionCourseDate === topic.dataID
    );
    
    // Check if any syllabus links have questionType matching this topic's dataID
    const hasSyllabusLinks = courseDesignState.some(item => 
      item.dataType === 'syllabuslink' && item.questionType === topic.dataID
    );
    
    return hasQuestions || hasSyllabusLinks;
  };

  // Delete handlers
  const handleDeleteClick = (item, event) => {
    if (event) event.stopPropagation();
    
    if (currentMode !== 'delete') return;
    
    // Toggle selection
    const newSelection = new Set(selectedForDeletion);
    if (newSelection.has(item.dataID)) {
      newSelection.delete(item.dataID);
    } else {
      newSelection.add(item.dataID);
    }
    setSelectedForDeletion(newSelection);
  };

  const handleDeleteSelected = () => {
    if (selectedForDeletion.size === 0) return;
    
    const itemsToDelete = courseDesignState.filter(item => 
      selectedForDeletion.has(item.dataID)
    );
    
    // Check if any selected topics have associated content
    const topicsWithContent = itemsToDelete.filter(item => 
      item.dataType === 'topic' && hasAssociatedContent(item)
    );
    
    if (topicsWithContent.length > 0) {
      // Show confirmation modal for topics with associated content
      setDeleteConfirmItem({ 
        isMultiple: true, 
        items: itemsToDelete,
        topicsWithContent: topicsWithContent,
        dataText: `${itemsToDelete.length} selected items`
      });
      setShowDeleteModal(true);
    } else {
      // Delete immediately without confirmation
      performDeletion(itemsToDelete);
    }
  };

  const performDeletion = (itemsToDelete) => {
    let newState = [...courseDesignState];
    
    // Remove all selected items and delete from database
    itemsToDelete.forEach(item => {
      newState = newState.filter(stateItem => stateItem.dataID !== item.dataID);
      removeFromDatabase(item.dataType, item.dataID);
    });
    
    updateState(newState);
    
    // Normalize after deletion
    setTimeout(() => {
      const normalizedState = validateDataOnLoad(newState);
      if (JSON.stringify(normalizedState) !== JSON.stringify(newState)) {
        console.log('Normalizing schedule data after deletion');
        updateState(normalizedState);
      }
    }, 100);
    
    // Clear selection
    setSelectedForDeletion(new Set());
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirmItem) return;

    if (deleteConfirmItem.isMultiple) {
      // Handle multiple item deletion
      const itemsToDelete = deleteConfirmItem.items;
      let newState = [...courseDesignState];
      
      // Remove all selected items and delete from database
      itemsToDelete.forEach(item => {
        newState = newState.filter(stateItem => stateItem.dataID !== item.dataID);
        removeFromDatabase(item.dataType, item.dataID);
      });
      
      updateState(newState);
      
      // Normalize after deletion
      setTimeout(() => {
        const normalizedState = validateDataOnLoad(newState);
        if (JSON.stringify(normalizedState) !== JSON.stringify(newState)) {
          console.log('Normalizing schedule data after multiple item deletion');
          updateState(normalizedState);
        }
      }, 100);
      
      // Clear selection
      setSelectedForDeletion(new Set());
    } else {
      // Handle single item deletion
      const newState = [...courseDesignState];
      
      // Remove the item from state
      const filteredState = newState.filter(item => item.dataID !== deleteConfirmItem.dataID);
      
      // Delete from database
      removeFromDatabase(deleteConfirmItem.dataType, deleteConfirmItem.dataID);
      
      // For all item types (including sections), just delete the single item
      updateState(filteredState);
      
      // Normalize after deletion
      setTimeout(() => {
        const normalizedState = validateDataOnLoad(filteredState);
        if (JSON.stringify(normalizedState) !== JSON.stringify(filteredState)) {
          console.log('Normalizing schedule data after item deletion');
          updateState(normalizedState);
        }
      }, 100);
    }

    // Close modal and reset
    setShowDeleteModal(false);
    setDeleteConfirmItem(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteConfirmItem(null);
  };

  // Arrow-based reordering functions
  const moveItemUp = (item) => {
    const newState = [...courseDesignState];
    
    // Get all schedule items (sections and topics) sorted by dataIndex
    const allScheduleItems = newState
      .filter(stateItem => stateItem.dataType === 'sectionName' || stateItem.dataType === 'topic')
      .sort((a, b) => parseInt(a.dataIndex) - parseInt(b.dataIndex));
    
    const currentIndex = allScheduleItems.findIndex(stateItem => stateItem.dataID === item.dataID);
    
    // Can't move up if already at the top
    if (currentIndex <= 0) return;
    
    // Swap with the item above
    const itemAbove = allScheduleItems[currentIndex - 1];
    const currentItem = allScheduleItems[currentIndex];
    
    // Swap their dataIndex values
    const tempIndex = currentItem.dataIndex;
    currentItem.dataIndex = itemAbove.dataIndex;
    itemAbove.dataIndex = tempIndex;
    
    // Update database
    updateDatabase(currentItem.dataType, currentItem.dataID, { dataIndex: currentItem.dataIndex });
    updateDatabase(itemAbove.dataType, itemAbove.dataID, { dataIndex: itemAbove.dataIndex });
    
    // Update state
    updateState(newState);
    
    // Normalize after reordering
    setTimeout(() => {
      const normalizedState = validateDataOnLoad(newState);
      if (JSON.stringify(normalizedState) !== JSON.stringify(newState)) {
        console.log('Normalizing schedule data after moving item up');
        updateState(normalizedState);
      }
    }, 100);
  };

  const moveItemDown = (item) => {
    const newState = [...courseDesignState];
    
    // Get all schedule items (sections and topics) sorted by dataIndex
    const allScheduleItems = newState
      .filter(stateItem => stateItem.dataType === 'sectionName' || stateItem.dataType === 'topic')
      .sort((a, b) => parseInt(a.dataIndex) - parseInt(b.dataIndex));
    
    const currentIndex = allScheduleItems.findIndex(stateItem => stateItem.dataID === item.dataID);
    
    // Can't move down if already at the bottom
    if (currentIndex >= allScheduleItems.length - 1) return;
    
    // Swap with the item below
    const itemBelow = allScheduleItems[currentIndex + 1];
    const currentItem = allScheduleItems[currentIndex];
    
    // Swap their dataIndex values
    const tempIndex = currentItem.dataIndex;
    currentItem.dataIndex = itemBelow.dataIndex;
    itemBelow.dataIndex = tempIndex;
    
    // Update database
    updateDatabase(currentItem.dataType, currentItem.dataID, { dataIndex: currentItem.dataIndex });
    updateDatabase(itemBelow.dataType, itemBelow.dataID, { dataIndex: itemBelow.dataIndex });
    
    // Update state
    updateState(newState);
    
    // Normalize after reordering
    setTimeout(() => {
      const normalizedState = validateDataOnLoad(newState);
      if (JSON.stringify(normalizedState) !== JSON.stringify(newState)) {
        console.log('Normalizing schedule data after moving item down');
        updateState(normalizedState);
      }
    }, 100);
  };

  // Mode Selector Component
  const ModeSelector = () => (
    <div className="mode-selector">
      <button 
        className={`mode-button ${currentMode === 'generateDays' ? 'active' : ''}`}
        onClick={() => handleModeChange('generateDays')}
        title="Generate class dates for semester"
      >
        <FontAwesomeIcon icon={faCalendarDays} />
        <span>Generate Days</span>
      </button>
      
      <button 
        className={`mode-button ${currentMode === 'add' ? 'active' : ''}`}
        onClick={() => handleModeChange('add')}
        title="Add new class events or sections"
      >
        <FontAwesomeIcon icon={faPlus} />
        <span>Add</span>
      </button>
      
      <button 
        className={`mode-button ${currentMode === 'delete' ? 'active' : ''}`}
        onClick={() => handleModeChange('delete')}
        title="Delete items from schedule"
      >
        <FontAwesomeIcon icon={faTrash} />
        <span>Delete</span>
      </button>
    </div>
  );

  // Schedule Item Components
  const SectionItem = ({ section, index }) => {
    const isEditing = editingItem === section.dataID;
    const isSelected = selectedForDeletion.has(section.dataID);
    
    const handleClick = () => {
      if (currentMode === 'delete') {
        handleDeleteClick(section);
      } else {
        // Always allow editing when not in delete mode
        setEditingItem(section.dataID);
      }
    };

    // Check if this is the first or last item for arrow button visibility
    const allScheduleItems = courseDesignState
      .filter(item => item.dataType === 'sectionName' || item.dataType === 'topic')
      .sort((a, b) => parseInt(a.dataIndex) - parseInt(b.dataIndex));
    const currentIndex = allScheduleItems.findIndex(item => item.dataID === section.dataID);
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === allScheduleItems.length - 1;
    
    return (
      <div 
        className={`section-item mode-${currentMode} ${isSelected ? 'selected-for-deletion' : ''}`} 
        data-index={index}
        onClick={!isEditing ? handleClick : undefined}
        style={{ 
          cursor: !isEditing ? 'pointer' : 'default',
          outline: isSelected ? '2px solid var(--color1)' : 'none'
        }}
      >
        <div className="section-content">
          {isEditing ? (
            <input 
              ref={inputRef}
              type="text" 
              defaultValue={section.dataText || 'Untitled Section'} 
              onBlur={(e) => {
                // Save to database on blur
                const newState = [...courseDesignState];
                const item = newState.find(s => s.dataID === section.dataID);
                if (item) {
                  item.dataText = e.target.value;
                  updateState(newState);
                }
                setEditingItem(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  // Save to database on Enter
                  const newState = [...courseDesignState];
                  const item = newState.find(s => s.dataID === section.dataID);
                  if (item) {
                    item.dataText = e.target.value;
                    updateState(newState);
                  }
                  setEditingItem(null);
                }
              }}
              className="section-input"
            />
          ) : (
            <span className="section-text">
              {section.dataText || 'Untitled Section'}
            </span>
          )}
        </div>
        {/* Always show reorder buttons on the right */}
        <div className="reorder-buttons">
          {!isFirst && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                moveItemUp(section);
              }}
              className="reorder-btn reorder-up"
              title="Move up"
            >
              <FontAwesomeIcon icon={faChevronUp} />
            </button>
          )}
          {!isLast && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                moveItemDown(section);
              }}
              className="reorder-btn reorder-down"
              title="Move down"
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          )}
        </div>
      </div>
    );
  };

  const DateTopicPair = ({ courseDate, topic, index }) => {
    const topicSelected = topic && selectedForDeletion.has(topic.dataID);
    const dateSelected = courseDate && selectedForDeletion.has(courseDate.dataID);
    const topicHasContent = topic && hasAssociatedContent(topic);
    const isEditingTopic = topic && editingItem === topic.dataID;
    const isEditingDate = courseDate && editingItem === courseDate.dataID;
    
    // For reordering purposes, use the topic if it exists, otherwise the courseDate
    const reorderItem = topic || courseDate;
    
    const handleTopicClick = (e) => {
      if (!topic) return;
      if (currentMode === 'delete') {
        handleDeleteClick(topic);
      } else {
        // Always allow editing when not in delete mode
        setEditingItem(topic.dataID);
      }
    };
    
    const handleDateClick = (e) => {
      if (!courseDate) return;
      if (currentMode === 'delete') {
        handleDeleteClick(courseDate);
      } else {
        // Always allow editing when not in delete mode
        setEditingItem(courseDate.dataID);
      }
    };

    // Check if this is the first or last item for arrow button visibility
    const allScheduleItems = courseDesignState
      .filter(item => item.dataType === 'sectionName' || item.dataType === 'topic')
      .sort((a, b) => parseInt(a.dataIndex) - parseInt(b.dataIndex));
    const currentIndex = reorderItem ? allScheduleItems.findIndex(item => item.dataID === reorderItem.dataID) : -1;
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === allScheduleItems.length - 1;
    
    return (
      <div className={`date-topic-pair`} data-index={index}>
        <div className="date-topic-content">
          {/* Date Section */}
          <div 
            className={`date-item ${currentMode === 'delete' ? 'deletable' : ''} ${dateSelected ? 'selected-for-deletion' : ''}`}
            onClick={!isEditingDate ? handleDateClick : undefined}
            style={{ 
              cursor: !isEditingDate ? 'pointer' : 'default',
              outline: dateSelected ? '2px solid var(--color1)' : 'none'
            }}
          >
          {courseDate ? (
            isEditingDate ? (
              <input
                ref={inputRef}
                type="date"
                defaultValue={courseDate.questionLogic || ''}
                onBlur={(e) => {
                  const newState = [...courseDesignState];
                  const item = newState.find(cd => cd.dataID === courseDate.dataID);
                  if (item && e.target.value) {
                    item.questionLogic = e.target.value;
                    item.dataText = formatDateForDisplay(e.target.value);
                    updateState(newState);
                    setTimeout(() => {
                      const normalizedState = validateDataOnLoad(newState);
                      if (JSON.stringify(normalizedState) !== JSON.stringify(newState)) {
                        console.log('Normalizing schedule data after editing date');
                        updateState(normalizedState);
                      }
                    }, 100);
                  }
                  setEditingItem(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const newState = [...courseDesignState];
                    const item = newState.find(cd => cd.dataID === courseDate.dataID);
                    if (item && e.target.value) {
                      item.questionLogic = e.target.value;
                      item.dataText = formatDateForDisplay(e.target.value);
                      updateState(newState);
                      setTimeout(() => {
                        const normalizedState = validateDataOnLoad(newState);
                        if (JSON.stringify(normalizedState) !== JSON.stringify(newState)) {
                          console.log('Normalizing schedule data after editing date');
                          updateState(normalizedState);
                        }
                      }, 100);
                    }
                    setEditingItem(null);
                  }
                }}
                className="date-input"
              />
            ) : (
              <span className="course-date">{courseDate.dataText}</span>
            )
          ) : (
            <span className="no-date">No date</span>
          )}
        </div>

        {/* Topic Section */}
        <div 
          className={`topic-item ${currentMode === 'delete' ? 'deletable' : ''} ${topicSelected ? 'selected-for-deletion' : ''} ${topicHasContent ? 'has-content' : ''}`}
          onClick={!isEditingTopic ? handleTopicClick : undefined}
          style={{ 
            cursor: !isEditingTopic ? 'pointer' : 'default',
            outline: topicSelected ? '2px solid var(--color1)' : 'none'
          }}
        >
          {topic ? (
            isEditingTopic ? (
              <textarea 
                ref={inputRef}
                defaultValue={topic.dataText || 'Click to add topic'} 
                onBlur={(e) => {
                  const newState = [...courseDesignState];
                  const item = newState.find(t => t.dataID === topic.dataID);
                  if (item) {
                    item.dataText = e.target.value;
                    updateState(newState);
                  }
                  setEditingItem(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    const newState = [...courseDesignState];
                    const item = newState.find(t => t.dataID === topic.dataID);
                    if (item) {
                      item.dataText = e.target.value;
                      updateState(newState);
                    }
                    setEditingItem(null);
                  }
                }}
                className="topic-input"
              />
            ) : (
              <span className="topic-text">
                {topic.dataText || 'Click to add topic'}
                {topicHasContent && <span className="content-indicator" title="Has associated content">*</span>}
              </span>
            )
          ) : (
            <span className="no-topic">No topic</span>
          )}
        </div>
        </div>
        
        {/* Always show reorder buttons on the right when reorderItem exists */}
        {reorderItem && (
          <div className="reorder-buttons">
            {!isFirst && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  moveItemUp(reorderItem);
                }}
                className="reorder-btn reorder-up"
                title="Move up"
              >
                <FontAwesomeIcon icon={faChevronUp} />
              </button>
            )}
            {!isLast && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  moveItemDown(reorderItem);
                }}
                className="reorder-btn reorder-down"
                title="Move down"
              >
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };
  // Generate Days Modal Component
  const GenerateDaysModal = () => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [meetingDays, setMeetingDays] = useState({
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false
    });

    // Load existing dates from state if they exist
    useEffect(() => {
      const startDateItem = courseDesignState.find(item => item.dataType === 'startDate');
      const endDateItem = courseDesignState.find(item => item.dataType === 'endDate');
      
      if (startDateItem) setStartDate(startDateItem.dataText);
      if (endDateItem) setEndDate(endDateItem.dataText);

      // Load meeting days
      const newMeetingDays = { ...meetingDays };
      Object.keys(newMeetingDays).forEach(day => {
        const meetingItem = courseDesignState.find(item => item.dataType === `meetsOn${day}`);
        if (meetingItem) {
          newMeetingDays[day] = meetingItem.dataText === 'true';
        }
      });
      setMeetingDays(newMeetingDays);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseDesignState]);

    const handleDayChange = (day, checked) => {
      setMeetingDays(prev => ({
        ...prev,
        [day]: checked
      }));
    };

    const handleGenerate = async () => {
      // Validate inputs
      if (!startDate || !endDate) {
        alert('Please enter both start and end dates.');
        return;
      }

      const selectedDays = Object.keys(meetingDays).filter(day => meetingDays[day]);
      if (selectedDays.length === 0) {
        alert('Please select at least one meeting day.');
        return;
      }

      // First, explicitly delete all auto-generated course dates from database
      const autoGeneratedCourseDates = courseDesignState.filter(item => 
        item.dataType === 'courseDate' && item.questionType === 'autoGenerated'
      );

      // Delete each auto-generated course date from database using context function
      autoGeneratedCourseDates.forEach(courseDate => {
        removeFromDatabase(courseDate.dataType, courseDate.dataID);
      });

      // Generate the new dates
      const newDates = generateClassDays(startDate, endDate, selectedDays);
      
      // Filter out auto-generated course dates from local state
      const filteredState = courseDesignState.filter(item => 
        !(item.dataType === 'courseDate' && item.questionType === 'autoGenerated')
      );

      // Add new auto-generated course dates
      newDates.forEach((dateString, index) => {
        const formattedDate = formatDateForDisplay(dateString);
        
        const newCourseDate = {
          dataType: 'courseDate',
          dataID: generateUniqueId(7),
          dataText: formattedDate,
          dataIndex: (index + 1).toString(),
          questionType: 'autoGenerated',
          questionLogic: dateString
        };
        
        filteredState.push(newCourseDate);
      });

      // Save meeting preferences to state
      Object.keys(meetingDays).forEach(day => {
        const existingItem = filteredState.find(item => item.dataType === `meetsOn${day}`);
        if (existingItem) {
          existingItem.dataText = meetingDays[day].toString();
        } else {
          filteredState.push({
            dataType: `meetsOn${day}`,
            dataID: generateUniqueId(7),
            dataText: meetingDays[day].toString(),
            dataIndex: '0'
          });
        }
      });

      // Save start and end dates
      const startDateItem = filteredState.find(item => item.dataType === 'startDate');
      if (startDateItem) {
        startDateItem.dataText = startDate;
      } else {
        filteredState.push({
          dataType: 'startDate',
          dataID: generateUniqueId(7),
          dataText: startDate,
          dataIndex: '0'
        });
      }

      const endDateItem = filteredState.find(item => item.dataType === 'endDate');
      if (endDateItem) {
        endDateItem.dataText = endDate;
      } else {
        filteredState.push({
          dataType: 'endDate',
          dataID: generateUniqueId(7),
          dataText: endDate,
          dataIndex: '0'
        });
      }

      // Update state and database - this will add the new dates
      updateState(filteredState);
      
      // Run normalization to organize the newly generated dates
      setTimeout(() => {
        const normalizedState = validateDataOnLoad(filteredState);
        if (JSON.stringify(normalizedState) !== JSON.stringify(filteredState)) {
          console.log('Normalizing schedule data after generating dates');
          updateState(normalizedState);
        }
      }, 100); // Small delay to ensure state update is complete
      
      setShowGenerateDaysModal(false);
    };

    return (
      <div className="modal-overlay">
        <div className="modal generate-days-modal">
          <h3>Generate Course Dates</h3>
          
          <div className="modal-section">
            <label>Start Date:</label>
            <input 
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="modal-section">
            <label>End Date:</label>
            <input 
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="modal-section">
            <label>Meeting Days:</label>
            <div className="days-checkboxes">
              {Object.keys(meetingDays).map(day => (
                <div key={day} className="checkbox-item">
                  <input 
                    type="checkbox" 
                    id={day}
                    checked={meetingDays[day]}
                    onChange={(e) => handleDayChange(day, e.target.checked)}
                  />
                  <label htmlFor={day}>{day}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button className="modal-button primary" onClick={handleGenerate}>
              Generate Dates
            </button>
            <button className="modal-button secondary" onClick={() => setShowGenerateDaysModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add Modal Component
  const AddModal = () => {
    const [addType, setAddType] = useState('');
    const [topicText, setTopicText] = useState('');
    const [sectionText, setSectionText] = useState('');
    const [classDate, setClassDate] = useState('');
    const [waitingForPosition, setWaitingForPosition] = useState(false);

    const handleAddClassEvent = () => {
      if (!topicText.trim()) {
        alert('Please enter a topic name.');
        return;
      }
      
      if (!classDate) {
        alert('Please select a class date.');
        return;
      }

      const newState = [...courseDesignState];
      
      // First, figure out what dataIndex the new courseDate should have
      // Get all existing courseDates and find where this date should be inserted chronologically
      const existingCourseDates = newState.filter(item => 
        item.dataType === 'courseDate' && item.questionLogic && /^\d{4}-\d{2}-\d{2}$/.test(item.questionLogic)
      );
      
      // Sort existing dates chronologically
      existingCourseDates.sort((a, b) => new Date(a.questionLogic) - new Date(b.questionLogic));
      
      let targetDataIndex;
      
      if (existingCourseDates.length === 0) {
        // No existing dates, start with 1
        targetDataIndex = '1';
      } else {
        // Find where this date fits chronologically
        const newDate = new Date(classDate);
        let insertPosition = existingCourseDates.length; // Default to end
        
        for (let i = 0; i < existingCourseDates.length; i++) {
          const existingDate = new Date(existingCourseDates[i].questionLogic);
          if (newDate < existingDate) {
            insertPosition = i;
            break;
          }
        }
        
        if (insertPosition === 0) {
          // Goes before all existing dates
          const firstIndex = parseFloat(existingCourseDates[0].dataIndex);
          targetDataIndex = (firstIndex - 1).toString();
        } else if (insertPosition === existingCourseDates.length) {
          // Goes after all existing dates
          const lastIndex = parseFloat(existingCourseDates[existingCourseDates.length - 1].dataIndex);
          targetDataIndex = (lastIndex + 1).toString();
        } else {
          // Goes between two existing dates
          const beforeIndex = parseFloat(existingCourseDates[insertPosition - 1].dataIndex);
          const afterIndex = parseFloat(existingCourseDates[insertPosition].dataIndex);
          targetDataIndex = ((beforeIndex + afterIndex) / 2).toString();
        }
      }

      // Create new topic with the determined dataIndex
      const newTopic = {
        dataType: 'topic',
        dataID: generateUniqueId(7),
        dataText: topicText.trim(),
        dataIndex: targetDataIndex, // Same as courseDate
        questionOptions: 'yes'
      };
      newState.push(newTopic);

      // Create new course date with the SAME dataIndex
      const newCourseDate = {
        dataType: 'courseDate',
        dataID: generateUniqueId(7),
        dataText: formatDateForDisplay(classDate),
        dataIndex: targetDataIndex, // Same as topic
        questionLogic: classDate,
        questionType: 'manual'
      };
      newState.push(newCourseDate);

      updateState(newState);
      
      // Run normalization to clean up any decimal indexes and ensure proper ordering
      setTimeout(() => {
        const normalizedState = validateDataOnLoad(newState);
        if (JSON.stringify(normalizedState) !== JSON.stringify(newState)) {
          console.log('Normalizing schedule data after adding class event');
          updateState(normalizedState);
        }
      }, 100);

      // Reset form and close modal
      setTopicText('');
      setClassDate('');
      setAddType('');
      setShowAddModal(false);
    };

    const handleAddSectionClick = () => {
      if (!sectionText.trim()) {
        alert('Please enter a section name.');
        return;
      }
      
      // Switch to position selection mode
      setWaitingForPosition(true);
    };

    const handlePositionSelect = (targetIndex) => {
      const newState = [...courseDesignState];
      
      // Create new section with dataIndex just before the target
      const targetIndexFloat = parseFloat(targetIndex);
      const newSectionIndex = (targetIndexFloat - 0.1).toString();

      const newSection = {
        dataType: 'sectionName',
        dataID: generateUniqueId(7),
        dataText: sectionText.trim(),
        dataIndex: newSectionIndex
      };
      newState.push(newSection);

      updateState(newState);
      
      // Run normalization to ensure proper ordering and clean up decimal indexes
      setTimeout(() => {
        const normalizedState = validateDataOnLoad(newState);
        if (JSON.stringify(normalizedState) !== JSON.stringify(newState)) {
          console.log('Normalizing schedule data after adding section');
          updateState(normalizedState);
        }
      }, 100);

      // Reset form and close modal
      setSectionText('');
      setAddType('');
      setWaitingForPosition(false);
      setShowAddModal(false);
    };

    if (waitingForPosition) {
      return (
        <div className="modal-overlay">
          <div className="modal position-select-modal">
            <p>Click the item you want the section header to precede.</p>
            
            <div className="position-select-preview">
              {getGroupedItems().map((group, idx) => (
                <div 
                  key={`position-${group.type}-${group.index}`} 
                  className="position-select-item"
                  onClick={() => handlePositionSelect(group.index)}
                >
                  {group.type === 'section' && (
                    <div className="preview-section">
                      {group.section.dataText}
                    </div>
                  )}
                  {group.type === 'dateTopicPair' && (
                    <div className="preview-topic">
                      {group.courseDate?.dataText || 'No date'} - {group.topic?.dataText || 'No topic'}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="modal-buttons">
              <button 
                className="modal-button secondary"
                onClick={() => {
                  setWaitingForPosition(false);
                  setAddType('');
                }}
              >
                Back to Form
              </button>
              <button 
                className="modal-button secondary"
                onClick={() => {
                  setWaitingForPosition(false);
                  setAddType('');
                  setSectionText('');
                  setShowAddModal(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="modal-overlay">
        <div className="modal add-modal">
          {!addType ? (
            <div className="add-options">
              <button 
                className="add-option-button"
                onClick={() => setAddType('classEvent')}
              >
                <div className="add-option-content">
                  <div className="add-option-title">Add Class Event</div>
                  <div className="add-option-description">
                    Add a new class event with its date
                  </div>
                </div>
              </button>
              
              <button 
                className="add-option-button"
                onClick={() => setAddType('section')}
              >
                <div className="add-option-content">
                  <div className="add-option-title">Add Section Name</div>
                  <div className="add-option-description">
                    Add a section header and choose where to place it in the schedule
                  </div>
                </div>
              </button>
            </div>
          ) : addType === 'classEvent' ? (
            <div className="add-form">
              <div className="form-group">
                <label>Topic/Class Title:</label>
                <textarea
                  value={topicText}
                  onChange={(e) => setTopicText(e.target.value)}
                  placeholder="Enter the topic or class title..."
                  rows="3"
                  className="form-textarea"
                />
              </div>
              
              <div className="form-group">
                <label>Class Date:</label>
                <input
                  type="date"
                  value={classDate}
                  onChange={(e) => setClassDate(e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              
              <div className="modal-buttons">
                <button 
                  className="modal-button primary"
                  onClick={handleAddClassEvent}
                >
                  Add Class Event
                </button>
                <button 
                  className="modal-button secondary"
                  onClick={() => setAddType('')}
                >
                  Back
                </button>
              </div>
            </div>
          ) : (
            <div className="add-form">
              <div className="form-group">
                <label>Section Name:</label>
                <input
                  type="text"
                  value={sectionText}
                  onChange={(e) => setSectionText(e.target.value)}
                  placeholder="Enter section name (e.g., 'Week 1', 'Unit 2', etc.)"
                  className="form-input"
                />
              </div>
              
              <div className="modal-buttons">
                <button 
                  className="modal-button primary"
                  onClick={handleAddSectionClick}
                >
                  Choose Position
                </button>
                <button 
                  className="modal-button secondary"
                  onClick={() => setAddType('')}
                >
                  Back
                </button>
              </div>
            </div>
          )}
          
          <div className="modal-buttons">
            <button 
              className="modal-button secondary"
              onClick={() => {
                setAddType('');
                setTopicText('');
                setSectionText('');
                setClassDate('');
                setWaitingForPosition(false);
                setShowAddModal(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const groupedItems = getGroupedItems();

  return (
    <div className="course-schedule-maker">
      <div className="course-sidebar">
        <ModeSelector />
      </div>
      
      <div className="main-content">
        <div className="schedule-content">
          {/* Debug info */}
          {groupedItems.length === 0 ? (
            <div className="empty-state">
              <p>No schedule items yet. Click "Generate Days" to create your class dates, then add topics and sections.</p>
            </div>
          ) : (
            groupedItems.map((group, idx) => (
              <div key={`${group.type}-${group.index}-${idx}`} className="schedule-group">
                {group.type === 'section' && (
                  <SectionItem section={group.section} index={group.index} />
                )}
                {group.type === 'dateTopicPair' && (
                  <DateTopicPair topic={group.topic} courseDate={group.courseDate} index={group.index} />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete Confirmation Panel */}
      {currentMode === 'delete' && selectedForDeletion.size > 0 && (
        <div className="delete-confirmation-panel">
          <div className="delete-panel-content">
            <h4>Items to be deleted:</h4>
            <div className="delete-items-list">
              {Array.from(selectedForDeletion).map(itemId => {
                const item = courseDesignState.find(cd => cd.dataID === itemId);
                if (!item) return null;
                
                const hasContent = item.dataType === 'topic' && hasAssociatedContent(item);
                
                return (
                  <div key={itemId} className={`delete-item ${hasContent ? 'has-content' : ''}`}>
                    <span className="delete-item-text">
                      {item.dataType === 'section' ? `Section: ${item.dataText}` : 
                       item.dataType === 'topic' ? `Topic: ${item.dataText}` :
                       `Date: ${item.dataText}`}
                    </span>
                    {hasContent && (
                      <span className="content-warning">Has associated content</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="delete-panel-buttons">
              <button 
                className="delete-cancel-btn"
                onClick={() => setSelectedForDeletion(new Set())}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-btn"
                onClick={handleDeleteSelected}
              >
                Delete Selected Items
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showGenerateDaysModal && <GenerateDaysModal />}
      
      {showAddModal && (
        <AddModal />
      )}
      
      {showDeleteModal && deleteConfirmItem && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <h3>Confirm Deletion</h3>
            {deleteConfirmItem.isMultiple ? (
              <>
                <p>
                  Are you sure you want to delete these <strong>{deleteConfirmItem.items.length} items</strong>?
                  {deleteConfirmItem.topicsWithContent && deleteConfirmItem.topicsWithContent.length > 0 && (
                    <>
                      <br/><br/>
                      <strong>Warning:</strong> {deleteConfirmItem.topicsWithContent.length} topic(s) have associated content (questions or syllabus links) that will also be deleted.
                    </>
                  )}
                </p>
                <div className="item-preview multiple-items">
                  {deleteConfirmItem.items.map(item => (
                    <div key={item.dataID} className="preview-item">
                      <strong>{item.dataType === 'sectionName' ? 'Section' :
                               item.dataType === 'topic' ? 'Topic' : 'Date'}:</strong> "{item.dataText}"
                      {item.dataType === 'topic' && hasAssociatedContent(item) && (
                        <span className="has-content-indicator"> (has content)</span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p>
                  Are you sure you want to delete this{' '}
                  <strong>
                    {deleteConfirmItem.dataType === 'sectionName' ? 'section' :
                     deleteConfirmItem.dataType === 'topic' ? 'topic' : 'date'}
                  </strong>?
                  {deleteConfirmItem.dataType === 'topic' && hasAssociatedContent(deleteConfirmItem) && (
                    <>
                      <br/><br/>
                      <strong>Warning:</strong> This topic has associated content (questions or syllabus links) that will also be deleted.
                    </>
                  )}
                </p>
                <div className="item-preview">
                  <strong>Item:</strong> "{deleteConfirmItem.dataText}"
                </div>
              </>
            )}
            
            <div className="modal-buttons">
              <button 
                className="cancel-button"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-button"
                onClick={handleConfirmDelete}
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseScheduleMaker;
